import { useEffect, useState } from 'react';

import { GOOGLE_MIC_SUPPORT_LINK } from 'src/utils/constants/ApiConstants';
import { VOICE_PERMISSION } from 'src/utils/constants/stringConstants';
import { VOICE_SEARCH_DEFAULT_LANGUAGE } from 'src/utils/constants/utilConstants';

import { useAppStore } from 'src/hooks/useAppStore';
import * as style from './SearchBoxStyle';
import {
  getMicMixpanelEvent,
  voiceErrorMixpanelEvent
} from './SearchMixpanelHelper';
import { RecognitionType } from './SearchTypes';

type VoiceSearchPropType = {
  setQueryString: (queryString: string) => void;
  setIsVoiceActive: (isVoiceActive: boolean) => void;
};

const VoiceSearch = ({
  setIsVoiceActive,
  setQueryString
}: VoiceSearchPropType) => {
  const { GRANTED, DENIED } = VOICE_PERMISSION;

  const { translations } = useAppStore();

  const headerStrings = translations?.common;
  const {
    SEARCH_DETAILS,
    SEARCH_TRY_AGAIN,
    SEARCH_HERE,
    SEARCH_DETAILS_INFO,
    SEARCH_SORRY,
    SEARCH_VOICE_TURNED_OFF,
    SEARCH_LISTENING
  } = headerStrings || {};

  const [messageText, setMessageText] = useState('');
  const [voiceIcon, setVoiceIcon] = useState('');
  const [toggleVoiceTurnedOffDetails, setToggleVoiceTurnedOffDetails] =
    useState(false);
  const [recognition, setRecognition] = useState<RecognitionType>();

  useEffect(() => {
    if (recognition) {
      getMic();
    } else {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        setRecognition(new SpeechRecognition());
      }
    }
  }, [recognition]);

  const replaceJSX = (find: string, replace: JSX.Element) => {
    return SEARCH_DETAILS_INFO.split(find).flatMap(
      (item: unknown, index: number) => (index == 0 ? [item, replace] : item)
    );
  };
  const replacedTextInfo = replaceJSX(
    '<here>',
    <span>
      <a
        rel="noopener noreferrer"
        href={GOOGLE_MIC_SUPPORT_LINK}
        target="_blank">
        {SEARCH_HERE}
      </a>
    </span>
  );

  const closeVoicePopup = () => {
    setIsVoiceActive(false);
    setToggleVoiceTurnedOffDetails(false);
    if (recognition) {
      recognition.stop();
      recognition.abort();
    }
  };

  const voiceTurnedOffDetails = () => {
    setToggleVoiceTurnedOffDetails(true);
  };

  const getSearcTextByMic = (micText: string) => {
    setQueryString(micText);
    const searchQueryUrl = `/search?q=${micText}`;
    window.location.href = searchQueryUrl;
  };

  const getMic = () => {
    let voicePermissionDisabled = false;
    let voicePermission: string | null = '';
    const permissionName = 'microphone' as PermissionName;
    getMicMixpanelEvent();
    navigator.permissions
      ?.query({ name: permissionName })
      ?.then((permissionStatus) => {
        voicePermission = permissionStatus.state;
        if (voicePermission === DENIED) {
          voicePermissionDisabled = true;
          voiceErrorMixpanelEvent(SEARCH_VOICE_TURNED_OFF);
        } else if (voicePermission === GRANTED) {
          voicePermissionDisabled = false;
        }
      });
    if (recognition) {
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-IN';
      let finalTranscript = '';

      recognition.start();
      recognition.onerror = () => {
        if (voicePermission === DENIED) {
          setMessageText(SEARCH_VOICE_TURNED_OFF);
          setVoiceIcon(VOICE_PERMISSION.PERMISSION_DENIED);
          voiceErrorMixpanelEvent(SEARCH_VOICE_TURNED_OFF);
        } else {
          setMessageText(SEARCH_SORRY);
          setVoiceIcon(VOICE_PERMISSION.ERROR);
          voiceErrorMixpanelEvent(SEARCH_SORRY);
        }
      };
      recognition.onend = () => {
        if (voicePermission === DENIED) {
          setMessageText(SEARCH_VOICE_TURNED_OFF);
          setVoiceIcon(VOICE_PERMISSION.PERMISSION_DENIED);
          voiceErrorMixpanelEvent(SEARCH_VOICE_TURNED_OFF);
        } else if (
          voicePermission === GRANTED &&
          !voicePermissionDisabled &&
          finalTranscript.trim().length === 0
        ) {
          setMessageText(SEARCH_SORRY);
          setVoiceIcon(VOICE_PERMISSION.ERROR);
          voiceErrorMixpanelEvent(SEARCH_SORRY);
        }
        if (
          !voicePermissionDisabled &&
          finalTranscript != null &&
          finalTranscript.trim().length > 0
        ) {
          setMessageText(finalTranscript);
          setVoiceIcon('');
          getSearcTextByMic(finalTranscript.trim());
          setIsVoiceActive(false);
        }
      };
      recognition.onstart = () => {
        setMessageText(SEARCH_LISTENING);
        setVoiceIcon('');
      };
      recognition.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const transcript = event.results[i][0].transcript;
          setMessageText(transcript);
          if (event.results[i].isFinal) finalTranscript += `${transcript} `;
        }
      };
    }
  };

  const retryVoiceCommand = () => {
    const timerId = setTimeout(() => {
      getMic();
      clearTimeout(timerId);
    }, 50);
  };
  return (
    <style.DialogWrapper data-testid="voice-search-container">
      <style.DialogContent>
        <style.CloseMicPopup
          onClick={closeVoicePopup}
          data-testid="close-mic-popup"></style.CloseMicPopup>
        <style.TextandiconWrapper>
          <style.MessageWrapper>
            <style.MessageText>
              {messageText}

              {voiceIcon === VOICE_PERMISSION.PERMISSION_DENIED && (
                <div
                  css={style.tryAgainText(toggleVoiceTurnedOffDetails)}
                  onClick={voiceTurnedOffDetails}
                  data-testid="voice-turned-off-details">
                  {SEARCH_DETAILS}
                </div>
              )}

              {toggleVoiceTurnedOffDetails && (
                <style.DetailsInfo>{replacedTextInfo}</style.DetailsInfo>
              )}
            </style.MessageText>
            {voiceIcon === VOICE_PERMISSION.ERROR && (
              <div
                css={style.tryAgainText()}
                onClick={retryVoiceCommand}
                data-testid="retry-voice-command">
                {SEARCH_TRY_AGAIN}
              </div>
            )}
          </style.MessageWrapper>
          {voiceIcon === VOICE_PERMISSION.ERROR ||
          voiceIcon === VOICE_PERMISSION.PERMISSION_DENIED ? (
            <style.CircleCss>
              <style.OuterCircle>
                <style.InnerCircle>
                  <style.VoiceMic />
                </style.InnerCircle>
              </style.OuterCircle>
              <style.ErrorIcon> ! </style.ErrorIcon>
            </style.CircleCss>
          ) : (
            <style.ListeningIcon />
          )}
        </style.TextandiconWrapper>
        <style.DefaultVoiceLanguage>
          {VOICE_SEARCH_DEFAULT_LANGUAGE}
        </style.DefaultVoiceLanguage>
      </style.DialogContent>
    </style.DialogWrapper>
  );
};

export default VoiceSearch;
